import { useCallback } from 'react';

import { useQuery, UseQueryOptions } from '@tanstack/react-query';

import { toQueryString } from '@utils/requests';

import { Application, ISearchApplication, OnboardApplication } from 'types/applications';
import { ILimitSearchResult } from 'types/search';
import { ITODO } from 'types/todo';

import { transformOnboardApplications } from './data-transformers';
import { request, unhandledRequest } from './request';

export function verifySsn(applicationId: number) {
  return request.patch({
    url: `/applications/${applicationId}/verify-ssn/`,
    data: { verify: true },
  });
}

export function editSsn(applicationId: number, ssn: string) {
  return request.patch({
    url: `/applications/${applicationId}/`,
    data: { ssn },
  });
}

export function edit(applicationId: number, data: ITODO) {
  return unhandledRequest.patch({
    url: `/applications/${applicationId}/`,
    data,
  });
}

export async function followers(applicationId: number) {
  const response = await unhandledRequest.get<number[]>({
    url: `/applications/${applicationId}/followers/`,
  });
  return response.data;
}

export function addFollowers(applicationId: number, user_ids: number[]) {
  return unhandledRequest.post({
    url: `/applications/${applicationId}/add-followers/`,
    data: { user_ids },
  });
}

export function keepOrMatchName(applicationId: number, keepOrMatch: ITODO) {
  return request.patch({
    url: `/applications/${applicationId}/verify-ssn/`,
    data: { keepOrMatch },
  });
}

export function setFollowers(applicationId: number, followers: ITODO) {
  return request.patch({
    url: `/applications/${applicationId}/`,
    data: { followers },
  });
}

export function create(data: ITODO) {
  return request.post({
    url: '/applications/',
    data,
  });
}

export async function search(queryString: string, pageNum?: number) {
  const response = await unhandledRequest.get<ILimitSearchResult<ISearchApplication>>({
    url: '/search/applications/' + queryString + (pageNum ? '&page=' + pageNum : ''),
  });

  return response.data;
}

export async function searchList(queryString: string, pageNum?: number) {
  const response = await unhandledRequest.get<ILimitSearchResult<ISearchApplication>>({
    url: '/search/applications-list/' + queryString + (pageNum ? '&page=' + pageNum : ''),
  });

  return response.data;
}

interface ExportData {
  fields: string[];
}

export function searchExport(queryString: string, data: ExportData) {
  return request.post({
    url: '/search/applications-export/' + queryString,
    data,
  });
}

export async function orSearch(queryString: string, pageNum?: number) {
  const response = await unhandledRequest.get<ILimitSearchResult<ISearchApplication>>({
    url: '/search/applicationsor/' + queryString + (pageNum ? '&page=' + pageNum : ''),
  });

  return response.data;
}

export async function orSearchList(queryString: string, pageNum?: number) {
  const response = await unhandledRequest.get<ILimitSearchResult<ISearchApplication>>({
    url: '/search/applicationsor-list/' + queryString + (pageNum ? '&page=' + pageNum : ''),
  });

  return response.data;
}

export function orSearchExport(queryString: string, data: ExportData) {
  return request.post({
    url: '/search/applicationsor-export/' + queryString,
    data,
  });
}

export function loadForJob(jobId: number) {
  const query = toQueryString({
    job: jobId,
    limit: 10000,
  });

  return request.get({
    url: '/search/applications/' + query,
  });
}

export function loadForJobMinimal(jobId: number) {
  const query = toQueryString({
    job: jobId,
    limit: 10000,
    minimal: 1,
  });

  return request.get({
    url: '/search/applications/' + query,
  });
}

export function load(applicationId: number) {
  return request.get({
    url: `/applications/${applicationId}/`,
  });
}

export async function loadFromDb(applicationId: number) {
  const response = await unhandledRequest.get<Application>({
    url: `/applications/${applicationId}/`,
  });
  return response.data;
}

export function loadBlob(url: string) {
  return request.get({ url, responseType: 'blob' });
}

export function findApplicationsByEmail(email: string) {
  const query = toQueryString({
    limit: 100,
    ordering: '-date',
    email__is: email,
  });

  return request.get({
    url: '/search/applications/' + query,
  });
}

export function read(applicationId: number, data: ITODO) {
  return request.patch({
    url: `/applications/${applicationId}/read/`,
    data,
  });
}

export function moveStage(applicationId: number, stageId: number) {
  return request.post({
    url: `/applications/${applicationId}/stage/${stageId}/`,
  });
}

export function addAttachment(applicationId: number, data: ITODO) {
  return request.post({
    url: `/applications/${applicationId}/attachments/`,
    data,
  });
}

export function removeAttachment(applicationId: number, attachmentId: number) {
  return request.delete({
    url: `/applications/${applicationId}/attachments/${attachmentId}/`,
  });
}

export function remove(applicationId: number) {
  return unhandledRequest.delete({
    url: `/applications/${applicationId}/`,
  });
}

export function unReject(applicationId: number) {
  return request.delete({
    url: `/applications/${applicationId}/reject/`,
  });
}

export function reject(applicationId: number, markNotified: boolean, emailData?: ITODO) {
  let data: ITODO = {
    isRejected: true,
    isNotifiedOfRejection: markNotified,
  };
  if (emailData) {
    data.emailData = emailData;
  }

  return request.post({
    url: `/applications/${applicationId}/reject/`,
    data,
  });
}

export function minimal(applicationId: number) {
  return request.get({
    url: `/applications/${applicationId}/minimal/`,
  });
}

export function hire(applicationId: number) {
  return unhandledRequest.post({
    url: `/applications/${applicationId}/hire/`,
  });
}

export function unHire(applicationId: number) {
  return unhandledRequest.delete({
    url: `/applications/${applicationId}/hire/`,
  });
}

export function retract(applicationId: number) {
  return request.patch({
    url: `/applications/${applicationId}/`,
    data: { isRetracted: true },
  });
}

export function unRetract(applicationId: number) {
  return request.patch({
    url: `/applications/${applicationId}/`,
    data: { isRetracted: false },
  });
}

export function onboard(applicationId: number, isOnboarded: boolean) {
  return request.patch({
    url: `/applications/${applicationId}/`,
    data: { isOnboarded },
  });
}

export function setAssignedTo(applicationId: number, userId: string | null) {
  return request.patch({
    url: `/applications/${applicationId}/`,
    data: { assignedTo: userId },
  });
}

export function addReview(data: ITODO) {
  return request.post({
    url: '/reviews/',
    data,
  });
}

export function addNote(data: ITODO) {
  return request.post({
    url: `/applicationchat/`,
    data,
  });
}
export function removeNote(noteId: number) {
  return request.delete({
    url: `/applicationchat/${noteId}/`,
  });
}

export function editNote(noteId: number, data: ITODO) {
  return request.patch({
    url: `/applicationchat/${noteId}/`,
    data,
  });
}

export async function loadIntegrations(applicationId: number, integrationType: ITODO) {
  const response = await unhandledRequest.get({
    url: `/applications/${applicationId}/integrations/${integrationType}/`,
  });
  return response.data;
}

export function loadIntegration(
  applicationId: number,
  integrationType: ITODO,
  integrationId: number
) {
  return request.get({
    url: `/applications/${applicationId}/integrations/${integrationType}/${integrationId}/`,
  });
}

export function removeTasklist(applicationId: number) {
  return request.delete({
    url: `/applications/${applicationId}/tasklist/`,
  });
}

export function loadTasks(applicationId: number) {
  return request.get({
    url: `/applications/${applicationId}/tasklist/`,
  });
}

export function checkTask(applicationId: number, taskId: number, checked: boolean) {
  return request.patch({
    url: `/applications/${applicationId}/tasklist/${taskId}/`,
    data: { checked },
  });
}

export function saveTasklist(applicationId: number, templateId: number) {
  return request.post({
    url: `/applications/${applicationId}/tasklist/`,
    data: { templateId },
  });
}

export function submitIntegration(
  applicationId: number,
  integrationType: ITODO,
  integrationId: number,
  data: ITODO,
  refetch = false
) {
  return request.post({
    url: `/applications/${applicationId}/integrations/${integrationType}/${integrationId}/`,
    data: { data, refetch },
  });
}

export function previewIntegration(
  applicationId: number,
  integrationType: ITODO,
  integrationId: number,
  data: ITODO
) {
  return request.post({
    url: `/applications/${applicationId}/integrations/${integrationType}/${integrationId}/preview/`,
    data: { data },
  });
}

export function deleteIntegration(
  applicationId: number,
  integrationType: ITODO,
  integrationId: number,
  modelId?: number
) {
  const query = toQueryString({
    id: modelId === undefined ? null : modelId,
  });
  return request.delete({
    url: `/applications/${applicationId}/integrations/${integrationType}/${integrationId}/` + query,
  });
}

export function sendMessage(applicationId: number, message: ITODO) {
  return request.post({
    url: `/applications/${applicationId}/message/`,
    data: message,
  });
}

export function loadForAllStages(jobId: number, order: number, limit = 10, offset = 0) {
  const query = toQueryString({
    job: jobId,
    limit,
    offset,
    ordering: order,
  });

  return request.get({
    url: '/search/applications/' + query,
  });
}

export async function loadCountForOnboarding() {
  const response = await unhandledRequest.get<ILimitSearchResult<any>>({
    url: `/search/onboarding/` + toQueryString({ limit: 1 }),
  });

  return response.data.count;
}

export async function loadCountForOnboarded() {
  const response = await unhandledRequest.get<ILimitSearchResult<any>>({
    url: `/search/onboarded/` + toQueryString({ limit: 1 }),
  });

  return response.data.count;
}

export async function loadCount() {
  const response = await unhandledRequest.get<number>({
    url: '/count/applications/',
  });

  return response.data;
}

export function loadCountForJob(jobId: number) {
  const query = toQueryString({
    job: jobId,
    limit: 1,
  });

  return request.get({
    url: `/search/applications/` + query,
  });
}

export function move(applicationId: number, data: ITODO) {
  return request.patch({
    url: `/applications/${applicationId}/move/`,
    data,
  });
}

const countTypeFunctionMap = {
  'applicants-count': loadCount,
  'onboarding-count': loadCountForOnboarding,
  'onboarded-count': loadCountForOnboarded,
};

const totalCountStaleTime = 5 * 60 * 1000;

type CountType = keyof typeof countTypeFunctionMap;

export function useTotalCount(countType: CountType) {
  return useQuery({
    queryKey: [countType],
    queryFn: countTypeFunctionMap[countType],
    staleTime: totalCountStaleTime,
  });
}

type OnboardingSearchResponse = ILimitSearchResult<
  Omit<OnboardApplication, 'sortedIsHiredTriggers'>
>;

export async function onboarding(queryString: string) {
  const response = await unhandledRequest.get<OnboardingSearchResponse>({
    url: '/search/onboarding/' + queryString,
  });

  return response.data;
}

export async function onboarded(queryString: string) {
  const response = await unhandledRequest.get<ILimitSearchResult<OnboardApplication>>({
    url: '/search/onboarded/' + queryString,
  });

  return response.data;
}

type UseOnboardListProps = UseQueryOptions<OnboardingSearchResponse> & {
  isCompleted?: boolean;
  queryString: string;
};

export function useOnboardList({ isCompleted, queryString, ...rest }: UseOnboardListProps) {
  return useQuery({
    queryKey: ['onboarding', queryString, isCompleted],
    ...rest,
    queryFn: () => (isCompleted ? onboarded(queryString) : onboarding(queryString)),
    select: useCallback((data) => transformOnboardApplications(data), []),
  });
}
